import TokenInputController from '@/src/components/Commons/Controllers/TokenInputController/TokenInputController';
import { FundWithDetailsEntity, ProtocolEntity, Token_With_Metadata_And_Amount, TokenWithAmountForDatum } from '@/src/lib/MayzSmartDB';
import { useTokensStore, useWalletStore } from '@/src/store';
import { ARROW_DOWN_UP } from '@/src/lib/SmartDB/Commons/Constants/images';
import BtnConfirmation from './Controls/BtnConfirmation';
import Commission from './ControlsPro/Commission';
import styles from './DepositForm.module.scss';
import InputTokensModal from './InputTokensModal/InputTokensModal';
import { useMemo } from 'react';
import { Button } from '@/src/components/Commons/Button';
import LoaderButton from '@/src/components/Commons/LoaderButton/LoaderButton';

interface DepositFormProps {
    protocol?: ProtocolEntity;
    fund: FundWithDetailsEntity;
    isDeposit: boolean;
    isMaxAmountLoaded: boolean;
    isTokensStoreReady: boolean;
    isAddingTokens: boolean;
    isValidInputTokens: boolean;
    isValidInputFT: boolean;
    setIsValidInputFT: (isValidInputFT: boolean) => void;
    inputTokens: Token_With_Metadata_And_Amount[];
    inputFT: bigint;
    commission_FT: bigint;
    real_FT: bigint;
    handleChange_InputTokens: (inputTokens: Token_With_Metadata_And_Amount[], isDeposit_?: boolean) => void;
    handleChange_InputFT: (inputFT: bigint, isDeposit_?: boolean) => void;
    handleClickToggleDepositWithdraw: () => void;
    showUTxOError: boolean;
    isProcessingArrangeUTxOTx: boolean;
    handleBtnArrangeUTxO: () => void;
    available_FT_in_Wallet_Based_On_Tokens: bigint;
    available_FT_in_Wallet: bigint;
    amount_Tokens_Available_In_Wallet: TokenWithAmountForDatum[];
    amount_Tokens_Available_In_Wallet_Based_On_FT: TokenWithAmountForDatum[];
    isValidTx: boolean;
    isProcessingTx: boolean;
    setShowProcessingTx: (show: boolean) => void;
    setShowUserConfirmation: (show: boolean) => void;
}

export default function DepositForm(props: DepositFormProps) {
    //--------------------------------------
    const tokensStore = useTokensStore();
    const walletStore = useWalletStore();
    //--------------------------------------
    const {
        protocol,
        fund,
        isDeposit,
        isTokensStoreReady,
        isAddingTokens,
        isValidInputTokens,
        isValidInputFT,
        setIsValidInputFT,
        inputTokens,
        inputFT,
        commission_FT,
        real_FT,
        handleChange_InputTokens,
        handleChange_InputFT,
        handleClickToggleDepositWithdraw,
        available_FT_in_Wallet_Based_On_Tokens,
        available_FT_in_Wallet,
        amount_Tokens_Available_In_Wallet,
        amount_Tokens_Available_In_Wallet_Based_On_FT,
        isValidTx,
        isProcessingTx,
        setShowProcessingTx,
        setShowUserConfirmation,
        isMaxAmountLoaded,
        showUTxOError,
        isProcessingArrangeUTxOTx,
        handleBtnArrangeUTxO,
    } = props;
    //--------------------------------------
    const image = useMemo(() => fund.tokenFT?.image, [fund.tokenFT]);
    const decimals = useMemo(() => fund.tokenFT?.decimals, [fund.tokenFT]);
    const ticker = useMemo(() => fund.tokenFT?.ticker, [fund.tokenFT]);
    //--------------------------------------
    return (
        <div className={styles.formController}>
            <div className={styles.inputController}>
                {isDeposit ? (
                    <>
                        <InputTokensModal
                            fund={fund}
                            isDeposit={isDeposit}
                            inputTokens={inputTokens}
                            setInputTokens={handleChange_InputTokens}
                            amount_Tokens_Available_In_Wallet={amount_Tokens_Available_In_Wallet}
                            amount_Tokens_Available_In_Wallet_Based_On_FT={amount_Tokens_Available_In_Wallet_Based_On_FT}
                            isValidInputTokens={isValidInputTokens}
                            isTokensStoreReady={isTokensStoreReady}
                            isMaxAmountLoaded={isMaxAmountLoaded}
                        />
                    </>
                ) : (
                    <>
                        <TokenInputController
                            CS={`${fund.getNET_id_CS()}`}
                            TN_Hex={`${fund.fdFundFT_TN_Hex}`}
                            ticker={ticker}
                            isTokensStoreReady={isTokensStoreReady}
                            image={image}
                            value={inputFT}
                            setValue={handleChange_InputFT}
                            min={0n}
                            max={available_FT_in_Wallet}
                            decimals={decimals}
                            isValidInput={isValidInputFT}
                            setIsValidInput={setIsValidInputFT}
                        />
                    </>
                )}
                <div className={styles.inputControllerSwitch}>
                    <div className={styles.switch} onClick={() => handleClickToggleDepositWithdraw()}>
                        <svg width="16" height="16" id="icon" className={`${styles.iconSwicht} ${!isDeposit ? styles.isSell : ''}`}>
                            <use href={ARROW_DOWN_UP}></use>
                        </svg>
                    </div>
                </div>

                {isDeposit ? (
                    <>
                        <TokenInputController
                            CS={`${fund.getNET_id_CS()}`}
                            TN_Hex={`${fund.fdFundFT_TN_Hex}`}
                            ticker={ticker}
                            isTokensStoreReady={isTokensStoreReady}
                            image={image}
                            value={inputFT}
                            setValue={handleChange_InputFT}
                            min={0n}
                            max={available_FT_in_Wallet_Based_On_Tokens}
                            decimals={decimals}
                            isValidInput={isValidInputFT}
                            setIsValidInput={setIsValidInputFT}
                        />
                    </>
                ) : (
                    <>
                        <InputTokensModal
                            fund={fund}
                            isDeposit={isDeposit}
                            inputTokens={inputTokens}
                            amount_Tokens_Available_In_Wallet={amount_Tokens_Available_In_Wallet}
                            amount_Tokens_Available_In_Wallet_Based_On_FT={amount_Tokens_Available_In_Wallet_Based_On_FT}
                            setInputTokens={handleChange_InputTokens}
                            isValidInputTokens={isValidInputTokens}
                            isTokensStoreReady={isTokensStoreReady}
                            isMaxAmountLoaded={isMaxAmountLoaded}
                        />
                    </>
                )}
            </div>

            {walletStore.swProInterface === true && (
                <div className={styles.swapProSection}>
                    <Commission fund={fund} isDeposit={isDeposit} commission_FT={commission_FT} real_FT={real_FT} />
                </div>
            )}

            <BtnConfirmation
                isDeposit={isDeposit}
                fund={fund}
                isTokensStoreReady={isTokensStoreReady}
                isAddingTokens={isAddingTokens}
                inputFT={inputFT}
                isValidTx={isValidTx}
                isProcessingTx={isProcessingTx}
                setShowProcessingTx={setShowProcessingTx}
                setShowUserConfirmation={setShowUserConfirmation}
                showUTxOError={showUTxOError}
                isProcessingArrangeUTxOTx={isProcessingArrangeUTxOTx}
                handleBtnArrangeUTxO={handleBtnArrangeUTxO}
            />
        </div>
    );
}
